/**
 * Role hierarchy and access control utilities
 */

export const ROLES = {
    PATIENT: 'patient',
    CLINICIAN: 'clinician',
    DOCTOR: 'doctor',
    ADMIN: 'admin'
};

export const ROLE_HIERARCHY = {
    [ROLES.PATIENT]: 1,
    [ROLES.CLINICIAN]: 2,
    [ROLES.DOCTOR]: 3,
    [ROLES.ADMIN]: 4
};

/**
 * Check if user has specific role
 */
export const hasRole = (user, role) => {
    return user && user.role === role;
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (user, roles) => {
    return user && roles.includes(user.role);
};

/**
 * Check if user has admin or doctor privileges
 */
export const hasAdminAccess = (user) => {
    return hasAnyRole(user, [ROLES.ADMIN, ROLES.DOCTOR]);
};

/**
 * Check if user is a patient
 */
export const isPatient = (user) => {
    return hasRole(user, ROLES.PATIENT);
};

/**
 * Get default route for user based on role
 */
export const getDefaultRoute = (user) => {
    if (!user || !user.role) return '/login';

    switch (user.role) {
        case ROLES.ADMIN:
        case ROLES.DOCTOR:
            return '/admin';
        case ROLES.PATIENT:
            return '/dashboard';
        default:
            return '/login';
    }
};

/**
 * Check if user can access a specific route
 */
export const canAccessRoute = (user, route) => {
    if (!user) return false;

    // Admin routes
    if (route.startsWith('/admin')) {
        return hasAdminAccess(user);
    }

    // Patient routes
    if (route.startsWith('/dashboard') || route.startsWith('/journal') || route.startsWith('/settings')) {
        return isPatient(user);
    }

    // Public routes
    if (['/login', '/register'].includes(route)) {
        return true;
    }

    return false;
};

/**
 * Validate user object structure
 */
export const isValidUser = (user) => {
    const hasUser = !!user;
    const hasId = !!(user?.id || user?._id);
    const hasRole = !!user?.role;
    // Trim the role and normalize it for comparison
    const normalizedRole = user?.role?.toString().trim().toLowerCase();
    const validRoles = Object.values(ROLES).map(role => role.toLowerCase());
    const validRole = normalizedRole && validRoles.includes(normalizedRole);

    // Debug when validation fails
    if (!hasUser || !hasId || !hasRole || !validRole) {
        console.log('User validation failed:', {
            hasUser,
            hasId,
            hasRole,
            validRole,
            userRole: user?.role,
            normalizedRole,
            availableRoles: Object.values(ROLES),
            validRoles
        });
    }

    return hasUser && hasId && hasRole && validRole;
};